import LoadingButton from '@/common/components/base/button/loadingButton';
import DateInput from '@/common/components/base/input/dateInput/DateInput';
import RadioGroup from '@/common/components/base/input/radioGroup/RadioGroup';
import TextInput from '@/common/components/base/input/textInput/TextInput';
import {
  ONLY_NUMBERS_RULE,
  PERSIAN_LATTER_RULE,
} from '@/common/components/base/input/textInput/textInput.rules';
import PercentageInput from '@/common/components/base/percentageInput/PercentageInput';
import { convertPersianToGregorian } from '@/common/utils/convertPersianToGregorian';
import { NATIONAL_CODE_RULE } from '@/common/utils/rules/nationalCode.rules';
import { requiredText } from '@/common/utils/rules/publicRules';
import { validateNotFutureDate } from '@/modules/adjusterReportModules/IncidentDetails/utils/incidentDetails.util';
import PersonalInquiry from '@/modules/adjusterReportModules/IncidentParticipants/components/common/personalInquiry/PersonalInquiry';
import { OtherDamagedInfoType } from '@/modules/adjusterReportModules/IncidentParticipants/pages/damaged/propertyDamage/driver-owner/otherDamagedInfo/OtherDamagedInfo.type';
import { PatchDamagedUserInformationQueryOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/patchDamagedUserInformation/patchDamagedUserInformationQueryOptions';
import ImagesFabMainModal from '@/modules/adjusterReportModules/adjusterReport/components/imagesFab/ImagesFabMainModal';
import { useMutation } from '@tanstack/react-query';
import { useFormContext } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { css } from 'styled-system/css';
import { Box, Flex } from 'styled-system/jsx';

const InitialOtherRealDamaged = () => {
  const methods = useFormContext<OtherDamagedInfoType>();
  const { mutate, isPending: loading } = useMutation(
    PatchDamagedUserInformationQueryOptions(),
  );
  const { id, damagedId } = useParams();
  const navigate = useNavigate();
  const onSubmit = (formData: OtherDamagedInfoType) => {
    mutate(
      {
        id: id as string,
        formData: {
          ...formData.personal_information,
          identity_number:
            formData?.personal_information?.identity_number &&
            formData?.personal_information?.identity_number !== ''
              ? formData?.personal_information?.identity_number
              : null,
          birthdate:
            formData.personal_information.birthdate &&
            convertPersianToGregorian(formData.personal_information.birthdate),
        },
        damaged_id: damagedId as string,
      },
      {
        onSuccess: () => {
          navigate('contact-info', { relative: 'path', replace: true });
        },
      },
    );
  };
  return (
    <Flex direction="column" gap={'1rem'} mb={24}>
      <Box py={24}>
        <PersonalInquiry outSideForm={useFormContext} />
      </Box>
      <Box>
        <Flex gap={79} mt={8}>
          <RadioGroup
            name="personal_information.owner_type"
            title="نوع مالکیت"
            options={[
              { label: 'حقیقی', value: 'real' },
              { label: 'حقوقی', value: 'legal' },
            ]}
            renderRadioButton={(Component) => <Box>{Component}</Box>}
            className={css({ gap: '2rem' })}
            rules={{ required: requiredText }}
          />
        </Flex>
      </Box>
      <Box>
        <Flex gap={79} mt={8}>
          <RadioGroup
            name="personal_information.gender"
            title="جنسیت مالک"
            options={[
              { label: 'آقا', value: 'male' },
              { label: 'خانم', value: 'female' },
            ]}
            renderRadioButton={(Component) => <Box>{Component}</Box>}
            className={css({ gap: '2rem' })}
            rules={{ required: requiredText }}
          />
        </Flex>
      </Box>
      <Box>
        <TextInput
          label="نام"
          name="personal_information.first_name"
          placeholder="نام را وارد کنید"
          rules={{ required: requiredText, ...PERSIAN_LATTER_RULE }}
          defaultValue=""
        />
      </Box>
      <Box>
        <TextInput
          label="نام ‌خانوادگی"
          name="personal_information.last_name"
          placeholder="نام ‌خانوادگی را وارد کنید"
          rules={{ required: requiredText, ...PERSIAN_LATTER_RULE }}
          defaultValue=""
        />
      </Box>
      <Box>
        <PercentageInput
          percentage={10}
          label="درصد تقصیر"
          maxPercentage={100}
          name="personal_information.fault_percentage"
          rules={{ required: requiredText, ...ONLY_NUMBERS_RULE }}
          placeHolder="درصد تقصیر را وارد کنید"
        />
      </Box>
      <Box>
        <DateInput
          label="تاریخ تولد"
          name="personal_information.birthdate"
          buttonText="انتخاب تاریخ"
          modalLabel="انتخاب تاریخ تولد"
          rules={{ validate: validateNotFutureDate, required: requiredText }}
        />
      </Box>
      <Box>
        <TextInput
          label="کدملی"
          name="personal_information.national_code"
          placeholder="کدملی را وارد کنید"
          rules={{ required: requiredText, ...NATIONAL_CODE_RULE }}
          defaultValue=""
          max={10}
        />
      </Box>
      <Box>
        <TextInput
          label="شماره شناسنامه"
          optionalLabel
          noCharInput
          name="personal_information.identity_number"
          placeholder="شماره شناسنامه را وارد کنید"
          rules={{ ...ONLY_NUMBERS_RULE }}
          defaultValue=""
        />
      </Box>
      <Box>
        <TextInput
          label="محل صدور شناسنامه"
          name="personal_information.identity_issue_location"
          placeholder="شماره شناسنامه را وارد کنید"
          rules={{ required: requiredText, ...PERSIAN_LATTER_RULE }}
          defaultValue=""
        />
      </Box>
      <Box w="100%">
        <LoadingButton
          onClick={methods.handleSubmit(onSubmit)}
          loading={loading}
          fullWidth
          disabled={!methods?.formState?.isValid}
        >
          ثبت و ادامه
        </LoadingButton>
      </Box>
      <ImagesFabMainModal state={'propertyDamaged'} />
    </Flex>
  );
};
export default InitialOtherRealDamaged;
